import {
  Container,
  Matrix,
  Mesh,
  Rectangle,
  Shader,
  Sprite,
  Texture,
  UniformGroup,
  WebGLRenderer,
  type MeshGeometry,
  type RenderOptions,
  type RenderTexture,
  type TextureSource,
} from 'pixi.js';
import type { FilterProbeResult, FrameTime, TransitionDefinition, VideoProvider, VideoFrameSource } from '../core/definitions';
import { computeGroupFrame, computeItemLayout, groupOriginShift } from '../core/geometry';
import type { GroupItem, Id, Project, Scene, SceneItem, SettingsBag, TransitionConfig, Vec2, VideoSettings } from '../core/model';
import type { Unsubscribe } from '../core/services';
import type {
  Compositor,
  CompositorDeps,
  CompositorView,
  DegradationPolicy,
  RenderStats,
  ProgramSourceEvidence,
  TransitionState,
  ViewTarget,
} from './api';
import { installBlendModes, pixiBlendMode } from './blend';
import { DegradationController } from './degradation';
import { clamp01 } from './easing';
import { FilterChain, clampResolution, createTarget, probeFilterPass, type RenderEnv } from './filterChain';
import { SourceScaleTracker } from './sourceScale';
import { consumedSources } from './source-evidence';
import { ProgramOutput } from './output';
import { PassRunner, PassShader, ShaderPrograms, createQuadGeometry } from './programs';
import { RESAMPLE_FRAGMENTS, resampleMode, type ResampleMode } from './resample';
import { FrameClock, WorkerTicker } from './scheduler';
import { TRANSITION_BUILTINS, fitRect, wrapTransitionFragment } from './shaderWrap';
import { TextureCache } from './textures';

/** Scenes nested deeper than this are not drawn. */
const MAX_NESTING_DEPTH = 8;
/** GPU resources of scenes and sources nobody has drawn for this long are released. */
const IDLE_RELEASE_MS = 5000;
/** A track write pending for this many frame intervals (and at least this long) means the sink is wedged. */
const OUTPUT_STALL_FRAMES = 30;
const OUTPUT_STALL_MIN_MS = 1000;
/** How long recovery waits for a delivered frame before escalating. */
const RECOVERY_WAIT_MS = 1500;
const TRANSPARENT: [number, number, number, number] = [0, 0, 0, 0];
const OPAQUE_BLACK: [number, number, number, number] = [0, 0, 0, 1];

interface ItemNode {
  item: SceneItem;
  /** Item the current sprite state was computed from. */
  applied: SceneItem | null;
  sprite: Sprite;
  texture: Texture;
  mesh: Mesh<MeshGeometry, Shader> | null;
  meshMode: ResampleMode | null;
  meshUniforms: { uTexSize: Float32Array; uRect: Float32Array } | null;
  /** True when the last layout produced something drawable. */
  drawable: boolean;
  natW: number;
  natH: number;
  sizeChanged: boolean;
  src: TextureSource | null;
  srcW: number;
  srcH: number;
  // Groups only.
  children: ItemNode[] | null;
  byId: Map<Id, ItemNode> | null;
  container: Container | null;
  target: RenderTexture | null;
  measured: SceneItem | null;
  groupW: number;
  groupH: number;
  /** Top-left of the group's drawn extent in group-local pixels (never positive). */
  groupMinX: number;
  groupMinY: number;
  /** The frame's min moved since the last layout although its size may not have. */
  minMoved: boolean;
  /** Largest on-canvas scale this node's picture is shown at, as of the last layout. */
  localScale: number;
  /** Canvas scale of everything above the node, as of the last pass. */
  inheritedScale: number;
}

interface SceneNode {
  id: Id;
  scene: Scene | null;
  root: Container;
  items: ItemNode[];
  target: RenderTexture | null;
  chain: FilterChain;
  /** Render pass that produced `output`. */
  pass: number;
  output: TextureSource | null;
  composing: boolean;
  lastUsedMs: number;
}

interface SourceEntry {
  chain: FilterChain;
  providerPass: number;
  provider: VideoProvider | null;
  picturePass: number;
  picture: TextureSource | null;
  /** Texture units per provider pixel (1 unless the media object's real size differs from the declared one). */
  scaleX: number;
  scaleY: number;
  lastUsedMs: number;
}

interface ActiveTransition {
  mode: 'timed' | 'manual';
  fromSceneId: Id | null;
  toSceneId: Id;
  config: TransitionConfig;
  definition: TransitionDefinition | undefined;
  settings: SettingsBag;
  shader: PassShader | null;
  /** Frame-clock time of the first frame that showed the transition; -1 until then. */
  startMs: number;
  durationMs: number;
  progress: number;
  resolve: (() => void) | null;
}

class View implements CompositorView {
  readonly canvas: HTMLCanvasElement;
  readonly context: CanvasRenderingContext2D | null;
  minIntervalMs = 0;
  nextDueMs = 0;
  dirty = true;
  destroyed = false;

  constructor(
    readonly target: ViewTarget,
    private readonly onDestroy: (view: View) => void,
  ) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = 0;
    this.canvas.height = 0;
    this.context = this.canvas.getContext('2d', { alpha: false });
  }

  setSize(width: number, height: number): void {
    const w = Math.max(0, Math.round(width));
    const h = Math.max(0, Math.round(height));
    if (this.canvas.width === w && this.canvas.height === h) return;
    this.canvas.width = w;
    this.canvas.height = h;
    this.dirty = true;
  }

  setMaxFps(fps: number): void {
    this.minIntervalMs = fps > 0 && Number.isFinite(fps) ? 1000 / fps : 0;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    // Releases the backing store even if the element stays referenced.
    this.canvas.width = 0;
    this.canvas.height = 0;
    this.onDestroy(this);
  }
}

let compositorGeneration=0;
class CompositorImpl implements Compositor {
  private readonly causalGeneration=++compositorGeneration;
  private canvas = document.createElement('canvas');
  private renderer: WebGLRenderer | null = null;
  private output: ProgramOutput | null = null;
  private programs: ShaderPrograms | null = null;
  private runner: PassRunner | null = null;
  private textures: TextureCache | null = null;
  private env: RenderEnv | null = null;
  private geometry: MeshGeometry | null = null;
  private initPromise: Promise<void> | null = null;
  private destroyed = false;
  private running = false;

  private width = 1920;
  private height = 1080;
  private fps = 30;
  private videoRef: VideoSettings | null = null;
  private renderScale = 1;

  private readonly clock = new FrameClock();
  private readonly ticker = new WorkerTicker(
    () => this.onTick(),
    (message) => this.deps.logger.log('error', 'render', `Frame clock worker failed: ${message}`),
  );
  private readonly degradation = new DegradationController();
  private timeBaseMs = 0;
  private lastTimeMs = 0;
  private hasRendered = false;
  private frameCount = 0;

  private project: Project | null = null;
  private scenesRef: Project['scenes'] | null = null;
  private sourcesRef: Project['sources'] | null = null;
  private pass = 0;
  private passNowMs = 0;
  private passTimeMs = 0;
  private readonly scenes = new Map<Id, SceneNode>();
  private readonly sources = new Map<Id, SourceEntry>();
  private readonly warned = new Set<string>();

  private programSceneId: Id | null = null;
  private previewSceneId: Id | null = null;
  private transition: ActiveTransition | null = null;
  private readonly transitionShaders = new Map<string, PassShader | null>();

  private readonly views = new Set<View>();
  private readonly screenRoot = new Container();
  // `dynamic` makes sprites follow later source and frame changes of their texture.
  private readonly screenTexture = new Texture({ source: Texture.EMPTY.source, dynamic: true });
  private readonly screenSprite = new Sprite(this.screenTexture);
  private readonly renderOptions: RenderOptions = { container: this.screenRoot };
  private readonly matrix = new Matrix();
  private readonly rect = [0, 0, 0, 0];

  private readonly stats: RenderStats;
  private windowStartMs = 0;
  private windowFrames = 0;
  private windowTimeMs = 0;
  private windowMaxMs = 0;
  private windowComposed = 0;
  private windowOverrunBase = 0;
  private lastErrorLogMs = -Infinity;
  private lastFrameWallMs: number | null = null;
  private lastComposedWallMs: number | null = null;
  private contextLost = false;
  private rebuilding: Promise<boolean> | null = null;
  private lastRebuildAttemptMs = -Infinity;
  private droppedBase = 0;
  private frameDelivered = false;
  private programSourceEvidence: ProgramSourceEvidence | null = null;
  private evidenceScene: Scene | null = null;
  private evidenceSources: Project['sources'] | null = null;
  private pendingProgramReceipt: { sceneId: Id; node: SceneNode; pass: number } | null = null;
  private readonly frameWaiters = new Set<(delivered: boolean) => void>();

  private readonly statsListeners = new Set<(stats: RenderStats) => void>();
  private readonly scaleListeners = new Set<(sourceId: Id, scale: number) => void>();
  private readonly scales = new SourceScaleTracker();
  private readonly transitionListeners = new Set<(state: TransitionState) => void>();
  private readonly streamListeners = new Set<(stream: MediaStream) => void>();
  private unsubscribeRegistry: Unsubscribe | null = null;

  constructor(private readonly deps: CompositorDeps) {
    this.screenRoot.addChild(this.screenSprite);
    this.stats = {
      backend: 'webgl',
      targetFps: this.fps,
      fps: 0,
      frameTimeAvgMs: 0,
      frameTimeMaxMs: 0,
      renderedFrames: 0,
      skippedFrames: 0,
      lateFrames: 0,
      droppedOutputFrames: 0,
      contextLost: false,
      failedFrames: 0,
      starvedFrames: 0,
      loopRestarts: 0,
      renderScale: 1,
      degraded: false,
      outputWidth: this.width,
      outputHeight: this.height,
    };
  }

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  init(): Promise<void> {
    if (this.destroyed) return Promise.reject(new Error('Compositor was destroyed'));
    this.initPromise ??= this.initRenderer();
    return this.initPromise;
  }

  private async initRenderer(): Promise<void> {
    const { deps } = this;
    const project = deps.getProject();
    this.videoRef = project.video;
    this.setVideo(project.video);
    this.programSceneId ??= project.scenes[project.currentSceneId] ? project.currentSceneId : null;
    if (deps.backend === 'webgpu') {
      // Filters and transitions are specified as GLSL, which the WebGPU backend cannot run.
      deps.logger.log('info', 'render', 'WebGPU backend requested; using WebGL2 because shaders are GLSL-only');
    }

    await this.createGpu();
    this.unsubscribeRegistry = deps.registry.subscribe(() => {
      if (this.env) this.env.registryVersion++;
    });
    this.output = new ProgramOutput(this.canvas, deps.logger);
    // A first black frame so consumers attached before start() see a live picture.
    this.presentProgram(null, null);
    this.output.push(0, OUTPUT_STALL_MIN_MS);
  }

  /** Creates the renderer on the current canvas and everything that lives on its context. */
  private async createGpu(): Promise<void> {
    const { deps } = this;
    const canvas = this.canvas;
    const renderer = new WebGLRenderer();
    await renderer.init({
      canvas,
      width: this.width,
      height: this.height,
      resolution: 1,
      autoDensity: false,
      antialias: false,
      backgroundColor: 0x000000,
      backgroundAlpha: 1,
      preserveDrawingBuffer: false,
      powerPreference: 'high-performance',
      preferWebGLVersion: 2,
      hello: false,
      skipExtensionImports: true,
    });
    if (this.destroyed) {
      renderer.destroy();
      throw new Error('Compositor was destroyed during init');
    }
    if (renderer.context.webGLVersion !== 2) {
      renderer.destroy();
      throw new Error('WebGL2 is required for compositing but is not available');
    }

    this.renderer = renderer;
    this.contextLost = false;
    this.stats.contextLost = false;
    installBlendModes(renderer);
    canvas.addEventListener('webglcontextlost', this.onContextLost);
    canvas.addEventListener('webglcontextrestored', this.onContextRestored);
    this.programs = new ShaderPrograms(renderer, deps.logger);
    this.runner = new PassRunner(renderer);
    this.textures = new TextureCache(renderer, deps.logger);
    this.geometry = createQuadGeometry();
    const textures = this.textures;
    this.env = {
      programs: this.programs,
      runner: this.runner,
      registry: deps.registry,
      assets: deps.assets,
      logger: deps.logger,
      registryVersion: 0,
      resolveTexture: (resource, version) => textures.acquire(resource, version, false, this.pass, this.passNowMs),
    };
  }

  /** Releases the renderer and every object created on its context; the document-side state stays. */
  private destroyGpu(): void {
    this.canvas.removeEventListener('webglcontextlost', this.onContextLost);
    this.canvas.removeEventListener('webglcontextrestored', this.onContextRestored);
    for (const node of this.scenes.values()) this.destroyScene(node);
    this.scenes.clear();
    for (const entry of this.sources.values()) entry.chain.destroy();
    this.sources.clear();
    for (const shader of this.transitionShaders.values()) shader?.destroy();
    this.transitionShaders.clear();
    this.screenSprite.visible = false;
    this.screenTexture.source = Texture.EMPTY.source;
    this.runner?.destroy();
    this.geometry?.destroy();
    this.textures?.destroy();
    this.runner = null;
    this.geometry = null;
    this.textures = null;
    this.programs = null;
    this.env = null;
    // Destroying the renderer deletes every GL object and loses the context, freeing its slot.
    this.renderer?.destroy();
    this.renderer = null;
    this.canvas.width = 0;
    this.canvas.height = 0;
  }

  /**
   * Replaces a lost or broken GPU context: a new canvas and renderer, with
   * scenes, textures and filters rebuilt from the document on the next frame.
   */
  private rebuildGpu(): Promise<boolean> {
    if (this.rebuilding) return this.rebuilding;
    this.lastRebuildAttemptMs = performance.now();
    this.stats.loopRestarts++;
    this.deps.logger.log('warn', 'render', 'Rebuilding the renderer on a new GPU context');
    const attempt = (async (): Promise<boolean> => {
      this.destroyGpu();
      this.canvas = document.createElement('canvas');
      try {
        await this.createGpu();
      } catch (error) {
        if (!this.destroyed) this.deps.logger.log('error', 'render', 'Could not create a new GPU context', error);
        return false;
      }
      // A generator track is fed frame objects and survives; a canvas capture track is tied to the old canvas.
      if (this.output?.mode === 'generator') this.output.setCanvas(this.canvas);
      else this.replaceOutput();
      const transition = this.transition;
      if (transition) transition.shader = this.transitionShader(transition.definition);
      for (const view of this.views) view.dirty = true;
      return true;
    })();
    this.rebuilding = attempt;
    void attempt.finally(() => {
      this.rebuilding = null;
    });
    return attempt;
  }

  /** New track and stream; consumers are told through onProgramStreamChanged. */
  private replaceOutput(): void {
    const previous = this.output;
    if (previous) {
      this.droppedBase += previous.droppedFrames;
      previous.destroy();
    }
    this.output = new ProgramOutput(this.canvas, this.deps.logger);
    this.deps.logger.log('warn', 'render', 'The program track was replaced');
    const stream = this.output.stream;
    for (const listener of [...this.streamListeners]) listener(stream);
  }

  start(): void {
    if (this.running || this.destroyed || !this.output) return;
    this.running = true;
    this.restartClock();
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    this.ticker.stop();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.running = false;
    this.ticker.destroy();
    this.unsubscribeRegistry?.();
    this.unsubscribeRegistry = null;
    const pending = this.transition;
    this.transition = null;
    pending?.resolve?.();
    this.output?.destroy();
    for (const view of [...this.views]) view.destroy();
    this.views.clear();
    this.statsListeners.clear();
    this.transitionListeners.clear();
    this.streamListeners.clear();
    for (const waiter of [...this.frameWaiters]) waiter(false);
    this.destroyGpu();
    this.screenRoot.destroy({ children: true });
    this.screenTexture.destroy(false);
    this.project = null;
  }

  private readonly onContextLost = (): void => {
    this.contextLost = true;
    this.stats.contextLost = true;
    this.deps.logger.log('error', 'render', 'WebGL context lost; rebuilding the renderer');
  };

  // The frame loop already replaces a lost context with a new one; a restore
  // of the old context that arrives first is handled the same way.
  private readonly onContextRestored = (): void => {
    if (!this.destroyed && this.contextLost) void this.rebuildGpu();
  };

  // -------------------------------------------------------------------------
  // Output and settings
  // -------------------------------------------------------------------------

  getProgramStream(): MediaStream {
    if (!this.output) throw new Error('Compositor.init() must complete before getProgramStream()');
    return this.output.stream;
  }

  getProgramSourceEvidence(): ProgramSourceEvidence | null {
    const proof = this.programSourceEvidence;
    const project = this.deps.getProject();
    const age = proof ? performance.now() - proof.monotonic_at_ms : NaN;
    if (!proof || !Number.isFinite(age) || age < 0 || age > 15_000 || this.destroyed || !this.renderer || this.contextLost || this.renderer.context.isLost || !this.frameDelivered || proof.scene_id !== this.programSceneId || proof.render_pass !== this.pass || project.scenes[proof.scene_id] !== this.evidenceScene || project.sources !== this.evidenceSources) return null;
    return { ...proof, sources: proof.sources.map((source) => ({ ...source, bounds: { ...source.bounds } })) };
  }

  onProgramStreamChanged(listener: (stream: MediaStream) => void): Unsubscribe {
    // The track survives scene, resolution and fps changes; it is only replaced by recovery
    // (a wedged track, or a rebuilt canvas when frames come from canvas capture).
    this.streamListeners.add(listener);
    return () => this.streamListeners.delete(listener);
  }

  applyVideoSettings(video: VideoSettings): void {
    const changedFps = Math.max(1, video.fps) !== this.fps;
    const changedSize = Math.max(2, Math.round(video.width)) !== this.width || Math.max(2, Math.round(video.height)) !== this.height;
    if (!changedFps && !changedSize) return;
    this.setVideo(video);
    if (changedSize) {
      this.renderer?.resize(this.width, this.height);
      for (const view of this.views) view.dirty = true;
    }
    if (changedFps && this.running) this.restartClock();
  }

  private setVideo(video: VideoSettings): void {
    this.width = Math.max(2, Math.round(video.width));
    this.height = Math.max(2, Math.round(video.height));
    this.fps = Math.max(1, video.fps);
    this.stats.targetFps = this.fps;
    this.stats.outputWidth = this.width;
    this.stats.outputHeight = this.height;
  }

  setDegradationPolicy(policy: DegradationPolicy): void {
    this.degradation.setPolicy(policy);
    this.setRenderScale(this.degradation.scale);
  }

  private setRenderScale(scale: number): void {
    this.renderScale = scale;
    this.stats.renderScale = scale;
    this.stats.degraded = scale < 1;
  }

  // -------------------------------------------------------------------------
  // Program, preview, transitions
  // -------------------------------------------------------------------------

  setProgramScene(sceneId: Id): void {
    const running = this.transition;
    this.transition = null;
    this.programSceneId = sceneId;
    running?.resolve?.();
    this.emitTransition();
  }

  getProgramScene(): Id | null {
    return this.programSceneId;
  }

  setPreviewScene(sceneId: Id | null): void {
    this.previewSceneId = sceneId;
    for (const view of this.views) if (view.target.type === 'preview') view.dirty = true;
  }

  startTransition(toSceneId: Id, transition: TransitionConfig, durationMs: number): Promise<void> {
    if (this.destroyed) return Promise.resolve();
    this.settleRunningTransition();
    const definition = this.deps.registry.getTransition(transition.kind);
    if (!definition) {
      this.deps.logger.log('warn', 'render', `Unknown transition "${transition.kind}"; cutting instead`);
    }
    const shader = this.transitionShader(definition);
    if (!shader || !(durationMs > 0) || toSceneId === this.programSceneId) {
      this.programSceneId = toSceneId;
      this.emitTransition();
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      this.transition = {
        mode: 'timed',
        fromSceneId: this.programSceneId,
        toSceneId,
        config: transition,
        definition,
        settings: { ...definition!.defaults(), ...transition.settings },
        shader,
        startMs: -1,
        durationMs,
        progress: 0,
        resolve,
      };
      this.emitTransition();
    });
  }

  setManualTransition(toSceneId: Id, transition: TransitionConfig, progress: number | null): void {
    if (this.destroyed) return;
    const current = this.transition;
    if (progress === null) {
      if (current?.mode === 'manual') {
        this.transition = null;
        this.emitTransition();
      }
      return;
    }
    const value = clamp01(progress);
    if (value >= 1) {
      this.settleRunningTransition();
      this.programSceneId = toSceneId;
      this.emitTransition();
      return;
    }
    let manual = current?.mode === 'manual' && current.toSceneId === toSceneId ? current : null;
    if (!manual || manual.config !== transition) {
      if (!manual) this.settleRunningTransition();
      const definition = this.deps.registry.getTransition(transition.kind);
      manual = {
        mode: 'manual',
        fromSceneId: this.programSceneId,
        toSceneId,
        config: transition,
        definition,
        settings: { ...definition?.defaults(), ...transition.settings },
        // A cut (or an unusable shader) shows the outgoing scene until the bar is committed.
        shader: this.transitionShader(definition),
        startMs: 0,
        durationMs: 0,
        progress: value,
        resolve: null,
      };
      this.transition = manual;
    }
    manual.progress = value;
    this.emitTransition();
  }

  getTransitionState(): TransitionState {
    const transition = this.transition;
    return transition
      ? { active: true, fromSceneId: transition.fromSceneId, toSceneId: transition.toSceneId, progress: transition.progress }
      : { active: false, fromSceneId: null, toSceneId: null, progress: 0 };
  }

  onTransitionState(listener: (state: TransitionState) => void): Unsubscribe {
    this.transitionListeners.add(listener);
    return () => this.transitionListeners.delete(listener);
  }

  /** A timed transition in flight is cut to its target; an uncommitted manual one is dropped. */
  private settleRunningTransition(): void {
    const running = this.transition;
    if (!running) return;
    this.transition = null;
    if (running.mode === 'timed') this.programSceneId = running.toSceneId;
    running.resolve?.();
  }

  private transitionShader(definition: TransitionDefinition | undefined): PassShader | null {
    if (!definition?.fragment || !this.programs) return null;
    const fragment = definition.fragment;
    let shader = this.transitionShaders.get(fragment);
    if (shader === undefined) {
      const program = this.programs.get(wrapTransitionFragment(fragment), `transition:${definition.kind}`);
      shader = program
        ? new PassShader(program, fragment, TRANSITION_BUILTINS, ['uFrom', 'uTo'], {
            uProgress: { value: 0, type: 'f32' },
            uStudio_uResolution: { value: new Float32Array(2), type: 'vec2<f32>' },
            uTime: { value: 0, type: 'f32' },
          })
        : null;
      this.transitionShaders.set(fragment, shader);
    }
    return shader;
  }

  private advanceTransition(timeMs: number): void {
    const transition = this.transition;
    if (!transition || transition.mode !== 'timed') return;
    if (transition.startMs < 0) transition.startMs = timeMs;
    const linear = (timeMs - transition.startMs) / transition.durationMs;
    if (linear >= 1) {
      this.transition = null;
      this.programSceneId = transition.toSceneId;
      this.emitTransition();
      transition.resolve?.();
      return;
    }
    const easing = transition.definition?.easing;
    transition.progress = clamp01(easing ? easing(linear, transition.settings) : linear);
    this.emitTransition();
  }

  private emitTransition(): void {
    if (this.transitionListeners.size === 0) return;
    const state = this.getTransitionState();
    for (const listener of [...this.transitionListeners]) listener(state);
  }

  // -------------------------------------------------------------------------
  // Views and capture
  // -------------------------------------------------------------------------

  createView(target: ViewTarget): CompositorView {
    const view = new View(target, (destroyed) => this.views.delete(destroyed));
    if (this.destroyed) view.destroy();
    else this.views.add(view);
    return view;
  }

  probeTexture(source: VideoFrameSource): boolean {
    if (this.destroyed || this.contextLost || !this.renderer || this.renderer.context.isLost || !this.textures || this.pass === 0) return false;
    return this.textures.probe(source) && !this.contextLost && !this.renderer.context.isLost;
  }

  probeFilterFragment(fragment: string, key: string, originSampler = false): FilterProbeResult {
    const gpu = { lost: () => this.destroyed || this.contextLost || !this.renderer || this.renderer.context.isLost, programs: this.programs, runner: this.runner };
    return probeFilterPass(gpu, { fragment, originSampler }, key);
  }

  async captureFrame(target: ViewTarget): Promise<Blob> {
    await this.init();
    if (!this.renderer || this.renderer.context.isLost) throw new Error('Compositor has no usable GPU context');
    const copy = document.createElement('canvas');
    copy.width = this.width;
    copy.height = this.height;
    const context = copy.getContext('2d', { alpha: false });
    if (!context) throw new Error('Could not create a 2D canvas for the capture');

    // Compose now, copy in the same task, then put the program picture back so
    // a capture of another target is never what the output track sees next.
    this.beginPass(this.deps.getProject());
    if (target.type === 'program') {
      this.drawProgram();
      context.drawImage(this.canvas, 0, 0);
    } else {
      this.drawTarget(target, context, this.width, this.height);
      this.drawProgram();
    }
    return new Promise<Blob>((resolve, reject) => {
      copy.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('PNG encoding failed'))), 'image/png');
    });
  }

  // -------------------------------------------------------------------------
  // Stats
  // -------------------------------------------------------------------------

  getStats(): RenderStats {
    this.stats.skippedFrames = this.clock.skippedFrames;
    this.stats.lateFrames = this.clock.lateFrames;
    this.stats.starvedFrames = this.clock.starvedFrames;
    this.stats.droppedOutputFrames = this.droppedBase + (this.output?.droppedFrames ?? 0);
    return { ...this.stats };
  }

  onSourceScale(listener: (sourceId: Id, scale: number) => void): Unsubscribe {
    this.scaleListeners.add(listener);
    return () => this.scaleListeners.delete(listener);
  }

  onStats(listener: (stats: RenderStats) => void): Unsubscribe {
    this.statsListeners.add(listener);
    return () => this.statsListeners.delete(listener);
  }

  // -------------------------------------------------------------------------
  // Frame loop
  // -------------------------------------------------------------------------

  getLastFrameTime(): number | null {
    return this.lastFrameWallMs;
  }

  restartLoop(): void {
    if (this.destroyed || !this.output) return;
    this.stats.loopRestarts++;
    const sinceMs = this.lastFrameWallMs === null ? null : Math.round(performance.now() - this.lastFrameWallMs);
    this.deps.logger.log('warn', 'render', `Frame clock restarted (restart #${this.stats.loopRestarts})`, {
      running: this.running,
      msSinceLastFrame: sinceMs,
    });
    // A fresh worker: the old one may have been terminated or may have stopped ticking.
    this.ticker.destroy();
    if (!this.running) return;
    // The slots nobody composed while the clock was down are lost frames.
    if (sinceMs !== null) {
      const lost = Math.max(0, Math.floor(sinceMs / this.clock.intervalMs) - 1);
      this.clock.skippedFrames += lost;
      this.clock.starvedFrames += lost;
    }
    this.restartClock();
  }

  async recover(): Promise<boolean> {
    if (this.destroyed || !this.initPromise) return false;
    try {
      await this.initPromise;
    } catch {
      return false;
    }
    if (this.destroyed || !this.running) return false;
    this.deps.logger.log('warn', 'render', 'Recovery requested');

    // 1. A fresh clock, on a fresh GPU context if the current one is gone, into a track that accepts frames.
    const lost = !this.renderer || this.contextLost || this.renderer.context.isLost;
    if (lost && !(await this.rebuildGpu())) return this.recoveryFailed('no GPU context could be created');
    if (this.destroyed || !this.running) return false;
    // A generator's sink cannot be reopened once it is wedged or errored, so the track has to change.
    if (this.output?.wedged) this.replaceOutput();
    const composedBefore = this.lastComposedWallMs;
    this.restartLoop();
    if (await this.nextDeliveredFrame()) return true;
    if (this.destroyed || !this.running) return false;

    // 2. Frames are composed but the track takes none: replace the track.
    if (this.lastComposedWallMs !== composedBefore) {
      this.replaceOutput();
      this.restartLoop();
      if (await this.nextDeliveredFrame()) return true;
      return this.recoveryFailed('the new track accepted no frame');
    }

    // 3. Nothing is composed although the context reports healthy: rebuild the renderer, then the track.
    if (!(await this.rebuildGpu())) return this.recoveryFailed('no GPU context could be created');
    if (this.destroyed || !this.running) return false;
    this.restartLoop();
    if (await this.nextDeliveredFrame()) return true;
    if (this.destroyed || !this.running) return false;
    this.replaceOutput();
    this.restartLoop();
    if (await this.nextDeliveredFrame()) return true;
    return this.recoveryFailed('no frame could be composed and delivered');
  }

  private recoveryFailed(reason: string): false {
    if (!this.destroyed) this.deps.logger.log('error', 'render', `Recovery failed: ${reason}`);
    return false;
  }

  /** Resolves true at the next frame that was composed and accepted by the track, false after a timeout. */
  private nextDeliveredFrame(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const settle = (delivered: boolean): void => {
        clearTimeout(timer);
        this.frameWaiters.delete(settle);
        resolve(delivered);
      };
      const timer = setTimeout(() => settle(false), RECOVERY_WAIT_MS);
      this.frameWaiters.add(settle);
    });
  }

  private restartClock(): void {
    const now = performance.now();
    // Frame time keeps counting across restarts so timestamps stay monotonic.
    this.timeBaseMs = this.hasRendered ? this.lastTimeMs + this.clock.intervalMs : 0;
    this.clock.reset(now, this.fps);
    this.windowStartMs = now;
    this.windowFrames = 0;
    this.windowTimeMs = 0;
    this.windowMaxMs = 0;
    this.windowComposed = 0;
    this.windowOverrunBase = this.clock.skippedFrames - this.clock.starvedFrames;
    this.ticker.start(now, this.clock.intervalMs);
  }

  private onTick(): void {
    if (!this.running || this.destroyed) return;
    const startedMs = performance.now();
    const slot = this.clock.tick(startedMs);
    if (slot < 0) return;

    const timeMs = this.timeBaseMs + slot * this.clock.intervalMs;
    const time: FrameTime = {
      timeMs,
      deltaMs: this.hasRendered ? timeMs - this.lastTimeMs : this.clock.intervalMs,
      frame: this.frameCount,
    };
    this.lastTimeMs = timeMs;
    this.hasRendered = true;
    this.frameCount++;
    let composed = false;
    this.frameDelivered = false;
    try {
      composed = this.renderFrame(time);
    } catch (error) {
      if (startedMs - this.lastErrorLogMs > IDLE_RELEASE_MS) {
        this.lastErrorLogMs = startedMs;
        this.deps.logger.log('error', 'render', 'Frame failed to render', error);
      }
    }
    if (this.destroyed) return;

    const finishedMs = performance.now();
    this.clock.frameDone(finishedMs);
    if (composed) {
      this.lastComposedWallMs = finishedMs;
      this.windowComposed++;
      const frameMs = finishedMs - startedMs;
      this.windowTimeMs += frameMs;
      if (frameMs > this.windowMaxMs) this.windowMaxMs = frameMs;
      // Liveness is a frame that reached the track; one the track did not take is a dropped output frame.
      if (this.frameDelivered) {
        this.lastFrameWallMs = finishedMs;
        this.stats.renderedFrames++;
        this.windowFrames++;
        for (const waiter of [...this.frameWaiters]) waiter(true);
      }
    } else {
      this.stats.failedFrames++;
      // Without a usable context nothing can be drawn: get a new one, at most one attempt a second.
      if ((!this.renderer || this.contextLost) && finishedMs - this.lastRebuildAttemptMs > 1000) void this.rebuildGpu();
    }
    if (finishedMs - this.windowStartMs >= 1000) this.closeStatsWindow(finishedMs);
  }

  private closeStatsWindow(nowMs: number): void {
    const stats = this.stats;
    const composed = this.windowComposed;
    stats.fps = (this.windowFrames * 1000) / (nowMs - this.windowStartMs);
    stats.frameTimeAvgMs = composed > 0 ? this.windowTimeMs / composed : 0;
    stats.frameTimeMaxMs = this.windowMaxMs;
    // Only slots lost to slow composing argue for a lower render scale; starved slots do not.
    const overrun = this.clock.skippedFrames - this.clock.starvedFrames;
    const scale = this.degradation.sample({
      frameTimeAvgMs: stats.frameTimeAvgMs,
      intervalMs: this.clock.intervalMs,
      renderedFrames: composed,
      skippedFrames: overrun - this.windowOverrunBase,
    });
    if (scale !== this.renderScale) this.setRenderScale(scale);
    this.windowStartMs = nowMs;
    this.windowFrames = 0;
    this.windowTimeMs = 0;
    this.windowMaxMs = 0;
    this.windowComposed = 0;
    this.windowOverrunBase = this.clock.skippedFrames - this.clock.starvedFrames;
    this.releaseIdle(nowMs - IDLE_RELEASE_MS);
    if (this.statsListeners.size > 0) {
      const snapshot = this.getStats();
      for (const listener of [...this.statsListeners]) listener(snapshot);
    }
  }

  /** Returns false when nothing could be composed (no usable GPU context). */
  private renderFrame(time: FrameTime): boolean {
    this.pendingProgramReceipt = null;
    try {
      this.deps.onBeforeFrame?.(time);
    } catch (error) {
      this.deps.logger.log('error', 'render', 'onBeforeFrame threw', error);
    }
    if (this.destroyed || !this.renderer) return false;
    if (this.contextLost || this.renderer.context.isLost) {
      this.contextLost = true;
      this.stats.contextLost = true;
      return false;
    }
    const project = this.deps.getProject();
    if (project.video !== this.videoRef) {
      this.videoRef = project.video;
      this.applyVideoSettings(project.video);
    }
    this.passTimeMs = time.timeMs;
    this.beginPass(project);
    this.advanceTransition(time.timeMs);

    // Views of anything other than program go through the canvas first; the
    // program picture is always the last thing drawn before the frame is taken.
    // Nobody can see a view while the document is hidden; composition and output carry on regardless.
    const viewsVisible = document.visibilityState !== 'hidden';
    let programViewDue = false;
    for (const view of this.views) {
      if (!viewsVisible) break;
      if (!view.context || view.canvas.width === 0 || view.canvas.height === 0) continue;
      if (!view.dirty && time.timeMs < view.nextDueMs) continue;
      if (view.target.type === 'program') {
        programViewDue = true;
        continue;
      }
      this.drawTarget(view.target, view.context, view.canvas.width, view.canvas.height);
      this.markDrawn(view, time.timeMs);
    }

    this.drawProgram();

    // A GPU draw alone cannot prove that the current program track consumed this frame.
    const receipt = this.pendingProgramReceipt as { sceneId: Id; node: SceneNode; pass: number } | null;
    this.pendingProgramReceipt = null;
    this.programSourceEvidence = null;
    let composition:import('@ohmyprime/segmenter').FrameComposition|undefined;
    let receiptValid=false;
    if (receipt && receipt.sceneId === this.programSceneId && receipt.pass === this.pass && this.scenes.get(receipt.sceneId) === receipt.node && receipt.node.pass === this.pass && !this.contextLost && !this.renderer.context.isLost) {
      const project = this.project;
      const sources = project ? consumedSources(receipt.node.items ?? [], { x: this.width, y: this.height }, (id, picture) => {
        const entry = this.sources.get(id);
        const kind = project.sources[id]?.kind ?? this.deps.getSourceKind?.(id);
        // The organic program layer reframes the delivered composite, not a
        // physical camera. Every uploaded visual source can contribute, except
        // the ad's own slot (which must not attest itself as creator content).
        return entry?.picturePass === this.pass && entry.providerPass === this.pass && entry.provider && entry.picture === picture && typeof kind === 'string' && /^[a-zA-Z0-9._:-]{1,128}$/.test(kind) && kind !== 'organic.slot' ? kind === 'media' ? 'video' : kind : null;
      }, (id, picture) => { const node = this.scenes.get(id); return node?.pass === this.pass && node.output === picture ? node.items : null; }) : [];
      const providers=new Map<Id,import('../core/definitions').VideoProvider>();
      for(const source of sources){const provider=this.sources.get(source.source_id)?.provider;if(provider)providers.set(source.source_id,{...provider});}
      receiptValid=true;
      try{if(this.output?.sourceId)composition=this.deps.composition?.({sceneId:receipt.sceneId,renderPass:this.pass,sources,providers,programFrameSourceId:this.output.sourceId,programFrameSourceGeneration:this.output.sourceGeneration,compositorGeneration:this.causalGeneration});}catch{}
      this.programSourceEvidence = sources.length > 0 ? { scene_id: receipt.sceneId, render_pass: this.pass, frame_seq: time.frame, time_ms: time.timeMs, monotonic_at_ms: performance.now(), sources } : null;
      this.evidenceScene = project?.scenes[receipt.sceneId] ?? null;
      this.evidenceSources = project?.sources ?? null;
    }

    this.frameDelivered=this.output?.push(time.timeMs,Math.max(OUTPUT_STALL_MIN_MS,OUTPUT_STALL_FRAMES*this.clock.intervalMs),this.programSceneId??undefined,composition)??false;
    if(this.frameDelivered&&receiptValid&&receipt){receipt.node.chain.notifyProgramApplied();this.deps.onProgramFrame?.({timeMs:time.timeMs,frame:time.frame,sceneId:receipt.sceneId});}
    if (!this.frameDelivered || !receipt) this.programSourceEvidence = null;

    if (programViewDue) {
      for (const view of this.views) {
        if (view.target.type !== 'program' || !view.context) continue;
        const cw = view.canvas.width;
        const ch = view.canvas.height;
        if (cw === 0 || ch === 0 || (!view.dirty && time.timeMs < view.nextDueMs)) continue;
        const rect = fitRect(this.width, this.height, cw, ch, this.rect);
        if (rect[2] !== cw || rect[3] !== ch) view.context.fillRect(0, 0, cw, ch);
        view.context.drawImage(this.canvas, 0, 0, this.width, this.height, rect[0]!, rect[1]!, rect[2]!, rect[3]!);
        this.markDrawn(view, time.timeMs);
      }
    }
    return true;
  }

  private markDrawn(view: View, timeMs: number): void {
    view.dirty = false;
    // Half a frame of slack so a 30 fps view on a 30 fps clock is not starved by rounding.
    view.nextDueMs = timeMs + view.minIntervalMs - this.clock.intervalMs / 2;
  }

  /** Starts a render pass: results cached "per frame" are keyed by the pass number. */
  private beginPass(project: Project): void {
    // What the previous pass drew, reported before its numbers are replaced.
    if (this.scaleListeners.size > 0) {
      this.scales.flush((id, scale) => {
        for (const listener of [...this.scaleListeners]) listener(id, scale);
      });
    }
    this.pass++;
    this.passNowMs = performance.now();
    this.project = project;
    if (project.scenes !== this.scenesRef) {
      this.scenesRef = project.scenes;
      for (const [id, node] of this.scenes) {
        if (!project.scenes[id]) {
          this.destroyScene(node);
          this.scenes.delete(id);
        }
      }
    }
    if (project.sources !== this.sourcesRef) {
      this.sourcesRef = project.sources;
      for (const [id, entry] of this.sources) {
        if (!project.sources[id]) {
          entry.chain.destroy();
          this.sources.delete(id);
          this.scales.forget(id);
        }
      }
    }
  }

  private releaseIdle(beforeMs: number): void {
    this.textures?.sweep(beforeMs);
    for (const entry of this.sources.values()) {
      if (entry.lastUsedMs < beforeMs) entry.chain.releaseTargets();
    }
    for (const [id, node] of this.scenes) {
      if (node.lastUsedMs < beforeMs) {
        this.destroyScene(node);
        this.scenes.delete(id);
      }
    }
  }

  // -------------------------------------------------------------------------
  // Drawing to the canvas
  // -------------------------------------------------------------------------

  private render(container: Container, target: RenderTexture | null, clearColor: [number, number, number, number]): void {
    const options = this.renderOptions;
    options.container = container;
    options.target = target ?? undefined;
    // render() caches the root transform on the options object; it must not leak between containers.
    options.transform = undefined;
    options.clear = true;
    options.clearColor = clearColor;
    this.renderer!.render(options);
  }

  /** Draws a picture at the canvas origin, scaled to w x h, over opaque black. */
  private drawToScreen(source: TextureSource | null, width: number, height: number): void {
    const sprite = this.screenSprite;
    if (source) {
      if (this.screenTexture.source !== source) {
        this.screenTexture.source = source;
        this.screenTexture.update();
      }
      sprite.visible = true;
      sprite.setSize(width, height);
    } else {
      sprite.visible = false;
    }
    this.render(this.screenRoot, null, OPAQUE_BLACK);
  }

  /** Composes and draws the program picture (or the running transition) onto the canvas. */
  private drawProgram(): void {
    const transition = this.transition;
    if (!transition) {
      this.presentProgram(this.programSceneId ? this.composeScene(this.programSceneId, 0) : null, null);
      return;
    }
    const from = transition.fromSceneId ? this.composeScene(transition.fromSceneId, 0) : null;
    if (!transition.shader) {
      this.presentProgram(from, null);
      return;
    }
    const to = this.composeScene(transition.toSceneId, 0);
    const shader = transition.shader;
    shader.setSampler('uFrom', from);
    shader.setSampler('uTo', to);
    shader.setFloat('uProgress', transition.progress);
    shader.setVec2('uStudio_uResolution', this.width, this.height);
    shader.setFloat('uTime', this.passTimeMs / 1000);
    try {
      shader.applyUser(transition.definition?.uniforms?.(transition.settings, transition.progress), this.env!.resolveTexture);
    } catch (error) {
      this.warnOnce(`transition-uniforms:${transition.config.kind}`, `Transition "${transition.config.kind}" failed to produce uniforms`, error);
    }
    this.presentProgram(null, shader);
  }

  private presentProgram(source: TextureSource | null, shader: PassShader | null): void {
    if (shader) this.runner!.draw(shader, null, this.width, this.height);
    else this.drawToScreen(source, this.width, this.height);
    // A transition or another target is not an exact rendering receipt for the current root scene.
    if (!shader && source && !this.contextLost && this.renderer && !this.renderer.context.isLost && this.programSceneId) {
      const node = this.scenes.get(this.programSceneId);
      if (node?.pass === this.pass && node.output === source) this.pendingProgramReceipt = { sceneId: this.programSceneId, node, pass: this.pass };
    }
  }

  /** Draws a non-program target, letterboxed, into a 2D canvas by way of the GL canvas. */
  private drawTarget(target: ViewTarget, context: CanvasRenderingContext2D, cw: number, ch: number): void {
    let source: TextureSource | null = null;
    let width = this.width;
    let height = this.height;
    if (target.type === 'preview') {
      source = this.previewSceneId ? this.composeScene(this.previewSceneId, 0) : null;
    } else if (target.type === 'scene') {
      source = this.composeScene(target.sceneId, 0);
    } else if (target.type === 'source') {
      const provider = this.providerOf(target.sourceId);
      if (provider) {
        source = this.pictureOf(target.sourceId);
        width = provider.width;
        height = provider.height;
      }
    }
    if (!source) {
      context.fillRect(0, 0, cw, ch);
      return;
    }
    const rect = fitRect(width, height, cw, ch, this.rect);
    const x = rect[0]!;
    const y = rect[1]!;
    const w = rect[2]!;
    const h = rect[3]!;
    // The blit cannot exceed the GL canvas; larger views are upscaled by the 2D copy.
    const bw = Math.min(w, this.width);
    const bh = Math.min(h, this.height);
    this.drawToScreen(source, bw, bh);
    if (w !== cw || h !== ch) context.fillRect(0, 0, cw, ch);
    context.drawImage(this.canvas, 0, 0, bw, bh, x, y, w, h);
  }

  // -------------------------------------------------------------------------
  // Sources
  // -------------------------------------------------------------------------

  private sourceEntry(sourceId: Id): SourceEntry {
    let entry = this.sources.get(sourceId);
    if (!entry) {
      entry = {
        chain: new FilterChain(this.env!),
        providerPass: -1,
        provider: null,
        picturePass: -1,
        picture: null,
        scaleX: 1,
        scaleY: 1,
        lastUsedMs: this.passNowMs,
      };
      this.sources.set(sourceId, entry);
    }
    return entry;
  }

  /** The source's provider for this pass; queried from the engine at most once per pass. */
  private providerOf(sourceId: Id): VideoProvider | null {
    const entry = this.sourceEntry(sourceId);
    if (entry.providerPass !== this.pass) {
      entry.providerPass = this.pass;
      let provider: VideoProvider | null = null;
      try {
        provider = this.deps.getSourceVideo(sourceId);
      } catch (error) {
        this.warnOnce(`provider:${sourceId}`, `getSourceVideo("${sourceId}") threw`, error);
      }
      entry.provider = provider && provider.width > 0 && provider.height > 0 ? provider : null;
    }
    return entry.provider;
  }

  /** The source's picture with its filter chain applied; uploaded and filtered at most once per pass. */
  private pictureOf(sourceId: Id): TextureSource | null {
    const entry = this.sourceEntry(sourceId);
    if (entry.picturePass === this.pass) return entry.picture;
    entry.picturePass = this.pass;
    entry.lastUsedMs = this.passNowMs;
    entry.picture = null;
    const provider = this.providerOf(sourceId);
    if (!provider) return null;
    // Frame-object providers replace `source` for every picture, so their texture is keyed by the source id.
    const key = provider.source instanceof VideoFrame ? `frames:${sourceId}` : provider.source;
    const base = this.textures!.acquire(provider.source, provider.version, provider.dynamic, this.pass, this.passNowMs, key);
    if (!base) return null;
    entry.chain.sync(this.project!.sources[sourceId]?.filters);
    // Source filters follow the degraded render scale too; they are often the most expensive passes.
    const picture = entry.chain.run(base, provider.width, provider.height, this.renderScale, this.passTimeMs);
    entry.picture = picture;
    if (picture === base) {
      entry.scaleX = base.width / provider.width;
      entry.scaleY = base.height / provider.height;
    } else {
      // Filter targets are allocated at the declared size (rounded up to whole pixels).
      entry.scaleX = 1;
      entry.scaleY = 1;
    }
    return picture;
  }

  // -------------------------------------------------------------------------
  // Scene graph
  // -------------------------------------------------------------------------

  /** Composes a scene (once per pass) and returns its picture, or null if it cannot be drawn. */
  private composeScene(sceneId: Id, depth: number, inherited = 1): TextureSource | null {
    const scene = this.project!.scenes[sceneId];
    if (!scene) return null;
    let node = this.scenes.get(sceneId);
    if (!node) {
      node = {
        id: sceneId,
        scene: null,
        root: new Container(),
        items: [],
        target: null,
        chain: new FilterChain(this.env!),
        pass: -1,
        output: null,
        composing: false,
        lastUsedMs: this.passNowMs,
      };
      this.scenes.set(sceneId, node);
    }
    node.lastUsedMs = this.passNowMs;
    if (node.pass === this.pass) return node.output;
    if (node.composing) {
      this.warnOnce(`cycle:${sceneId}`, `Scene "${scene.name}" contains itself; the recursive item is not drawn`);
      return null;
    }
    if (depth > MAX_NESTING_DEPTH) {
      this.warnOnce(`depth:${sceneId}`, `Scene "${scene.name}" is nested more than ${MAX_NESTING_DEPTH} levels deep and is not drawn`);
      return null;
    }

    node.composing = true;
    try {
      if (node.scene !== scene) {
        node.items = this.syncItems(scene.items, node.items, node.root);
        node.scene = scene;
      }
      this.updateItems(node.items, depth, true, inherited);
      const resolution = clampResolution(this.width, this.height, this.renderScale);
      if (!node.target) {
        node.target = createTarget(this.width, this.height, resolution);
      } else {
        const source = node.target.source;
        if (source.width !== this.width || source.height !== this.height || source.resolution !== resolution) {
          node.target.resize(this.width, this.height, resolution);
        }
      }
      this.render(node.root, node.target, TRANSPARENT);
      node.chain.sync(scene.filters);
      node.output = node.chain.run(node.target.source, this.width, this.height, resolution, this.passTimeMs);
      node.pass = this.pass;
    } finally {
      node.composing = false;
    }
    return node.output;
  }

  /** Rebuilds the retained nodes of one item list, reusing nodes whose id and kind are unchanged. */
  private syncItems(items: readonly SceneItem[], previous: ItemNode[], parent: Container): ItemNode[] {
    const reusable = new Map<Id, ItemNode>();
    for (const node of previous) reusable.set(node.item.id, node);
    const next: ItemNode[] = [];
    for (const item of items) {
      let node = reusable.get(item.id);
      if (node && node.item.type === item.type) {
        reusable.delete(item.id);
      } else {
        node = this.createItemNode(item);
      }
      if (item.type === 'group') {
        const before = node.children && node.item.type === 'group' ? node.item.children : null;
        if (before !== item.children) {
          node.children = this.syncItems(item.children, node.children ?? [], node.container!);
          node.byId = new Map(node.children.map((child) => [child.item.id, child]));
        }
      }
      node.item = item;
      next.push(node);
    }
    for (const node of reusable.values()) this.destroyItemNode(node);
    parent.removeChildren();
    for (const node of next) {
      parent.addChild(node.sprite);
      if (node.mesh) parent.addChild(node.mesh);
    }
    return next;
  }

  private createItemNode(item: SceneItem): ItemNode {
    const texture = new Texture({ source: Texture.EMPTY.source, frame: new Rectangle(0, 0, 1, 1), dynamic: true });
    const sprite = new Sprite(texture);
    sprite.visible = false;
    return {
      item,
      applied: null,
      sprite,
      texture,
      mesh: null,
      meshMode: null,
      meshUniforms: null,
      drawable: false,
      natW: 0,
      natH: 0,
      sizeChanged: false,
      src: null,
      srcW: 0,
      srcH: 0,
      children: null,
      byId: null,
      container: item.type === 'group' ? new Container() : null,
      target: null,
      measured: null,
      groupW: 0,
      groupH: 0,
      groupMinX: 0,
      groupMinY: 0,
      minMoved: false,
      localScale: 1,
      inheritedScale: 1,
    };
  }

  private destroyItemNode(node: ItemNode): void {
    if (node.children) for (const child of node.children) this.destroyItemNode(child);
    node.children = null;
    node.sprite.destroy();
    node.texture.destroy(false);
    if (node.mesh) {
      const shader = node.mesh.shader;
      node.mesh.destroy();
      shader?.destroy();
      node.mesh = null;
    }
    node.container?.destroy();
    node.target?.destroy(true);
    node.target = null;
  }

  private destroyScene(node: SceneNode): void {
    for (const item of node.items) this.destroyItemNode(item);
    node.items = [];
    node.root.destroy();
    node.chain.destroy();
    node.target?.destroy(true);
    node.target = null;
    node.output = null;
  }

  private updateItems(nodes: ItemNode[], depth: number, draw: boolean, inherited = 1): void {
    for (let i = 0; i < nodes.length; i++) this.updateItem(nodes[i]!, depth, draw, inherited);
  }

  /**
   * Per-pass refresh of one item: resolves its picture and natural size and
   * re-applies the layout only when the item, its size or its texture changed.
   * With `draw` false only the natural size is refreshed (for group measuring).
   */
  private updateItem(node: ItemNode, depth: number, draw: boolean, inherited: number): void {
    const item = node.item;
    node.inheritedScale = inherited;
    const visible = draw && item.visible;
    let width = 0;
    let height = 0;
    let scaleX = 1;
    let scaleY = 1;
    let source: TextureSource | null = null;

    if (item.type === 'source') {
      const provider = this.providerOf(item.sourceId);
      if (provider) {
        width = provider.width;
        height = provider.height;
        if (visible) {
          source = this.pictureOf(item.sourceId);
          const entry = this.sources.get(item.sourceId)!;
          scaleX = entry.scaleX;
          scaleY = entry.scaleY;
          // The scale this item is shown at on the output, as of its last layout.
          this.scales.note(item.sourceId, inherited * node.localScale * this.renderScale);
        }
      }
    } else if (item.type === 'scene') {
      width = this.width;
      height = this.height;
      if (visible) source = this.composeScene(item.sceneId, depth + 1, inherited * node.localScale);
    } else {
      this.updateItems(node.children!, depth, visible, inherited * node.localScale);
      this.measureGroup(node, item);
      width = node.groupW;
      height = node.groupH;
      if (visible && width > 0 && height > 0) source = this.renderGroup(node, width, height);
    }

    node.sizeChanged = width !== node.natW || height !== node.natH || node.minMoved;
    node.minMoved = false;
    node.natW = width;
    node.natH = height;

    if (!visible || !source || width <= 0 || height <= 0) {
      node.sprite.visible = false;
      if (node.mesh) node.mesh.visible = false;
      return;
    }
    if (
      node.applied !== item ||
      node.sizeChanged ||
      node.src !== source ||
      node.srcW !== source.width ||
      node.srcH !== source.height
    ) {
      this.applyLayout(node, item, source, scaleX, scaleY);
    }
    const useMesh = node.meshMode !== null;
    node.sprite.visible = node.drawable && !useMesh;
    if (node.mesh) node.mesh.visible = node.drawable && useMesh;
  }

  private measureGroup(node: ItemNode, item: GroupItem): void {
    const children = node.children!;
    let dirty = node.measured !== item;
    for (let i = 0; i < children.length && !dirty; i++) dirty = children[i]!.sizeChanged;
    if (!dirty) return;
    node.measured = item;
    const byId = node.byId!;
    // The drawn extent is the children's boxes unioned with the local origin, so a child
    // at negative coordinates is rendered rather than clipped. Group children report
    // their own frame, because their picture is displaced by it.
    const frame = computeGroupFrame(item.children, (child) => {
      const childNode = byId.get(child.id);
      const size: Vec2 = { x: childNode?.natW ?? 0, y: childNode?.natH ?? 0 };
      return child.type === 'group' && childNode ? { size, min: { x: childNode.groupMinX, y: childNode.groupMinY } } : { size };
    });
    node.groupW = frame.size.x;
    node.groupH = frame.size.y;
    if (frame.min.x !== node.groupMinX || frame.min.y !== node.groupMinY) node.minMoved = true;
    node.groupMinX = frame.min.x;
    node.groupMinY = frame.min.y;
  }

  private renderGroup(node: ItemNode, width: number, height: number): TextureSource {
    const w = Math.max(1, Math.ceil(width));
    const h = Math.max(1, Math.ceil(height));
    // Rasterise at the scale the group is shown at (in quarter steps, at most 4x) so a
    // scaled-up group stays sharp; the logical size, and so the layout, is unchanged.
    const shown = node.inheritedScale * node.localScale;
    const boost = Math.min(4, Math.max(1, Math.ceil(shown * 4) / 4));
    const resolution = clampResolution(w, h, this.renderScale * boost);
    if (!node.target) {
      node.target = createTarget(w, h, resolution);
    } else {
      const source = node.target.source;
      if (source.width !== w || source.height !== h || source.resolution !== resolution) node.target.resize(w, h, resolution);
    }
    // Local coordinates start at the frame's min, so shift the content into the target.
    node.container!.position.set(-node.groupMinX, -node.groupMinY);
    this.render(node.container!, node.target, TRANSPARENT);
    return node.target.source;
  }

  private applyLayout(node: ItemNode, item: SceneItem, source: TextureSource, scaleX: number, scaleY: number): void {
    node.applied = item;
    node.src = source;
    node.srcW = source.width;
    node.srcH = source.height;

    const layout = computeItemLayout(item.transform, { x: node.natW, y: node.natH });
    // A group's picture starts at its frame's min; the shift keeps its local origin in place.
    const shift =
      item.type === 'group' ? groupOriginShift(item.transform, { x: node.groupMinX, y: node.groupMinY }) : null;
    const m = shift ? { ...layout.matrix, tx: layout.matrix.tx + shift.x, ty: layout.matrix.ty + shift.y } : layout.matrix;
    node.localScale = Math.max(Math.hypot(m.a, m.b), Math.hypot(m.c, m.d));
    const contentW = layout.contentSize.x;
    const contentH = layout.contentSize.y;
    const determinant = m.a * m.d - m.b * m.c;
    node.drawable = contentW > 0 && contentH > 0 && Math.abs(determinant) > 1e-12 && Number.isFinite(determinant);
    if (!node.drawable) return;

    // Cropped region in texture units.
    const crop = item.transform.crop;
    const frameX = Math.min(Math.max(0, crop.left), node.natW) * scaleX;
    const frameY = Math.min(Math.max(0, crop.top), node.natH) * scaleY;
    const frameW = contentW * scaleX;
    const frameH = contentH * scaleY;
    const blendMode = pixiBlendMode(item.blendMode);
    const alpha = clamp01(item.opacity);

    const mode = resampleMode(item.scaleFilter);
    const mesh = mode ? this.resampleMesh(node, mode) : null;
    node.meshMode = mesh ? mode : null;
    if (mesh) {
      const uniforms = node.meshUniforms!;
      mesh.shader!.resources.uTexture = source;
      uniforms.uTexSize[0] = source.width;
      uniforms.uTexSize[1] = source.height;
      uniforms.uRect[0] = frameX;
      uniforms.uRect[1] = frameY;
      uniforms.uRect[2] = frameW;
      uniforms.uRect[3] = frameH;
      // The quad is the unit square, so the layout matrix is scaled by the content size.
      mesh.setFromMatrix(this.matrix.set(m.a * contentW, m.b * contentW, m.c * contentH, m.d * contentH, m.tx, m.ty));
      mesh.blendMode = blendMode;
      mesh.alpha = alpha;
      return;
    }

    const texture = node.texture;
    if (texture.source !== source) texture.source = source;
    texture.frame.x = frameX;
    texture.frame.y = frameY;
    texture.frame.width = frameW;
    texture.frame.height = frameH;
    texture.update();
    const sprite = node.sprite;
    // Sprite space is texture units; the layout matrix expects content pixels.
    sprite.setFromMatrix(this.matrix.set(m.a / scaleX, m.b / scaleX, m.c / scaleY, m.d / scaleY, m.tx, m.ty));
    sprite.blendMode = blendMode;
    sprite.alpha = alpha;
  }

  private resampleMesh(node: ItemNode, mode: ResampleMode): Mesh<MeshGeometry, Shader> | null {
    if (node.mesh && node.meshMode === mode) return node.mesh;
    const program = this.programs!.get(RESAMPLE_FRAGMENTS[mode], `scale-${mode}`);
    if (!program) return null;
    const group = new UniformGroup({
      uTexSize: { value: new Float32Array(2), type: 'vec2<f32>' },
      uRect: { value: new Float32Array(4), type: 'vec4<f32>' },
    });
    const shader = new Shader({ glProgram: program, resources: { uTexture: Texture.EMPTY.source, uStudioItem: group } });
    node.meshUniforms = group.uniforms as { uTexSize: Float32Array; uRect: Float32Array };
    if (node.mesh) {
      const previous = node.mesh.shader;
      node.mesh.shader = shader;
      previous?.destroy();
    } else {
      node.mesh = new Mesh({ geometry: this.geometry!, shader, texture: Texture.WHITE });
      node.mesh.visible = false;
      // Keep the item's place in the draw order: right above its (hidden) sprite.
      const parent = node.sprite.parent;
      if (parent) parent.addChildAt(node.mesh, parent.getChildIndex(node.sprite) + 1);
    }
    return node.mesh;
  }

  private warnOnce(key: string, message: string, detail?: unknown): void {
    if (this.warned.has(key)) return;
    this.warned.add(key);
    this.deps.logger.log('warn', 'render', message, detail);
  }
}

export function createCompositor(deps: CompositorDeps): Compositor {
  return new CompositorImpl(deps);
}
